import React, { useState, useEffect } from "react";
import { Grid as GridLoading } from "react-loader-spinner";
import html2canvas from "html2canvas";
import Highlighter from "react-highlight-words";
import SlotCounter from "react-slot-counter";
import { TypeAnimation } from "react-type-animation";
import "../assets/styles/compliance/Compliance.scss";
import { styled } from "@mui/material/styles";
import { theme } from "../theme";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import ComplianceSearch from "./compliance/ComplianceSearch";
import DoughnutChart from "./compliance/DoughnutChart";
import FadeIn from "./compliance/FadeIn";
import GaugeChart from "./compliance/GaugeChart";
import GaugeChartSentiment from "./compliance/GaugeChartSentiment";
import Markdown from "react-markdown";

// Import MUI elements
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Skeleton from "@mui/material/Skeleton";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import RadioGroup from "@mui/material/RadioGroup";
import { Grid, Paper } from "@mui/material";

// Import framework to manage datepicker
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// Import icons
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";
import SummarizeIcon from "@mui/icons-material/Summarize";
import SquareIcon from "@mui/icons-material/Square";

ChartJS.register(
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Item = styled(Paper)(({ theme }) => ({
  padding: 10,
}));

const ModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 850,
    height: 600,
    bgcolor: 'background.paper',
    border: '1px solid #000',
    p: 4,
    overflow: 'scroll',
    display: 'block'
};

const TranscriptModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1250,
  height: 800,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
};

const ImageStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxHeight: 800,
    bgcolor: 'background.paper',
    border: '1px solid #000',
    p: 4,
    overflow: 'scroll',
    display: 'block',
    padding: 0
};

function formatDateString(dateString: string) {
  const date = new Date(dateString);
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
  return formattedDate;
}

function extractDuration(durationString: string): [number, number, number] {
  const parts = durationString.split(", ");
  let years = 0,
    months = 0,
    days = 0;

  for (const part of parts) {
    if (part.includes("year")) {
      years = parseInt(part.split(" ")[0]);
    } else if (part.includes("month")) {
      months = parseInt(part.split(" ")[0]);
    } else if (part.includes("day")) {
      days = parseInt(part.split(" ")[0]);
    }
  }

  return [years, months, days];
}

function Compliance({ parentToChild }: { parentToChild: any }) {
  const { mode } = parentToChild;

  // Set the default call center
  const [selectedCallCenter, setSelectedCallCenter] = useState<string>("CC001");
  const [selectedRadio, setSelectedRadio] = useState<string>("resolution_rate");

  const [complianceData, setComplianceData] = useState<any>(null);
  // const [agentComplianceData, setAgentComplianceData] = useState<any>(null);
  const [selectedAgentId, setSelectedAgentId] = useState("FL3PJ06");
  const [agentList, setAgentList] = useState<any[]>([]);

  // const [numbersAsInt, setNumbersAsInt] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [alertData, setAlertData] = useState<any>(null);
  const [alertText, setAlertText] = useState<string[]>([]);

  // Add state for selected option and start date
  const [selectedOption, setSelectedOption] = useState<string>("Last 7 days");
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs("2024-01-31"));
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(dayjs("2024-01-25"));

  // Variables for sending a report
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [frequency, setFrequency] = useState<string>("Daily");
  const [recurrences, setRecurrences] = useState<string>("1");
  const [rangeStartDate, setRangeStartDate] = useState<dayjs.Dayjs>(dayjs());
  const [rangeEndDate, setRangeEndDate] = useState<dayjs.Dayjs>(dayjs());
  const Today = dayjs();

  const [scriptedChoice, setScriptedChoice] = useState(
    "Scripted_Opening_Exists"
  );
  const [agentData, setAgentData] = useState<any>();
  const [agentTenure, setAgentTenure] = useState<[number, number, number]>([
    0, 0, 0,
  ]);

  const [graphData, setGraphData] = useState<any>();
  const [agentSentimentResolData, setAgentSentimentResolData] = useState<any>();
  const [agentPromotionTranscriptData, setAgentPromotionTranscriptData] =
    useState<any>();
  const [agentGoodTranscriptData, setAgentGoodTranscriptData] = useState<any>();
  const [agentBadTranscriptData, setAgentBadTranscriptData] = useState<any>();
  const [feedbackData, setFeedbackData] = useState<any>();
  const [isFeedbackLoading, setIsFeedbackLoading] = useState<boolean>(true);
  const [promoSuggestion, setPromoSuggestion] = useState<string>("");

  const [clickedLabel, setClickedLabel] = useState(null);
  const [layout, setLayout] = React.useState<string | null>("overview");
  const [expandClicked, setExpandClicked] = useState<boolean>(false);

  const [sentimentResolData, setSentimentResolData] = useState<any>({
    prohibitedData: [],
    missedData: [],
    callerData: [],
    openingData: [],
    closingData: [],
    themeData: [],
    recordData: [],
    recFailureData: [],
    themeFailureData: [],
  });
  const [metricsData, setMetricsData] = useState<any>({
    prohibitedData: [],
    missedData: [],
    callerData: [],
    openingData: [],
    closingData: [],
    themeData: [],
    recordData: [],
    recFailureData: [],
    themeFailureData: [],
  });

  const [selectedCallType, setSelectedCallType] = useState<string>("All");
  const [selectedCallReason, setSelectedCallReason] = useState<string>("All");

  const [callTypeList, setCallTypeList] = useState<string[]>([]);
  const [callReasonList, setCallReasonList] = useState<string[]>([]);

  // const callTypeList = ['All', 'Existing Customer', 'Information and Guidance', 'Services Issue Resolution'];
  // const callReasonList = ['All', 'Balance Transfer', 'Credit Line', 'Payment', 'Transaction'];

  useEffect(() => {
    setCallTypeList([
      "All",
      "Existing Customer",
      "Information and Guidance",
      "Services Issue Resolution",
      "Service Orientation",
      "Information and Education",
      "Resolution and Follow-Up",
    ]);

    if (
      selectedCallType === "Service Orientation" ||
      selectedCallType === "Information and Education" ||
      selectedCallType === "Resolution and Follow-Up"
    ) {
      setCallReasonList([
        "All",
        "Shopping for rate",
        "Preapproval",
        "Rate lock",
        "Low appraisal",
        "Not qualified for requested amount",
        "Missing documents",
      ]);
    } else if (
      selectedCallType === "Existing Customer" ||
      selectedCallType === "Information and Guidance" ||
      selectedCallType === "Services Issue Resolution"
    ) {
      setCallReasonList([
        "All",
        "Balance Transfer",
        "Credit Line",
        "Payment",
        "Transaction",
      ]);
    } else {
      setCallReasonList([
        "All",
        "Balance Transfer",
        "Credit Line",
        "Payment",
        "Transaction",
        "Shopping for rate",
        "Preapproval",
        "Rate lock",
        "Low appraisal",
        "Not qualified for requested amount",
        "Missing documents",
      ]);
    }

    if (
      (selectedCallType === "Service Orientation" ||
        selectedCallType === "Information and Education" ||
        selectedCallType === "Resolution and Follow-Up") &&
      (selectedCallReason === "Balance Transfer" ||
        selectedCallReason === "Credit Line" ||
        selectedCallReason === "Payment" ||
        selectedCallReason === "Transaction")
    ) {
      setSelectedCallReason("All");
    } else if (
      (selectedCallType === "Existing Customer" ||
        selectedCallType === "Information and Guidance" ||
        selectedCallType === "Services Issue Resolution") &&
      (selectedCallReason === "Shopping for rate" ||
        selectedCallReason === "Preapproval" ||
        selectedCallReason === "Rate lock" ||
        selectedCallReason === "Low appraisal" ||
        selectedCallReason === "Not qualified for requested amount" ||
        selectedCallReason === "Missing documents")
    ) {
      setSelectedCallReason("All");
    }
  }, [selectedCallType, selectedCallReason]);

  const handleLayout = (
    event: React.MouseEvent<HTMLElement>,
    newLayout: string | null
  ) => {
    if (newLayout !== null) {
      setLayout(newLayout);
    }
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      datalabels: {
        display: false,
        color: mode === "light" ? "black" : "white",
        labels: {
          value: {
            color: mode === "light" ? "black" : "white",
          },
        },
      },
      legend: {
        display: true,
        labels: {
          boxWidth: 10,
          color: mode === "light" ? "#797979" : "#b3b3b3",
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: false,
          text: "Date",
        },
        ticks: {
          color: mode === "light" ? "#797979" : "#b3b3b3",
        },
        grid: {
          color: mode === "light" ? "#e4e4e4" : "#5e5e5e",
        },
        // min: 1,
        // max: 10,
      },
      y: {
        title: {
          display: true,
          text: "Scripted Opening (%)",
        },
        ticks: {
          color: mode === "light" ? "#797979" : "#b3b3b3",
        },
        grid: {
          color: mode === "light" ? "#e4e4e4" : "#5e5e5e",
        },
      },
    },
  };

  const data = {
    labels: graphData && graphData[1],
    datasets: [
      {
        label: "Agent",
        data: graphData && graphData[0],
        backgroundColor: theme.colors.primary,
        borderColor: mode === "light" ? "#2e2e38" : theme.colors.primary,
        pointRadius: 5,
      },
      {
        label: "Avg Peers",
        data: graphData && graphData[2],
        backgroundColor: mode === "light" ? "#747480" : "#747480",
        borderColor: mode === "light" ? "#747480" : "#747480",
        pointRadius: 5,
      },
      {
        label: "Top Decile",
        data: graphData && graphData[3],
        backgroundColor: mode === "light" ? "#b7ad6c" : "#b7ad6c",
        borderColor: mode === "light" ? "#b7ad6c" : "#b7ad6c",
        pointRadius: 5,
      },
    ],
  };

  const handleAgentIdChange = (
    newSelection: string,
    selectedAgentId: string
  ) => {
    if (newSelection !== selectedAgentId) {
      setSelectedAgentId(newSelection);
    }
  };

  const handleCallTypeChange = (
    newSelection: string,
    selectedCallType: string
  ) => {
    if (newSelection !== selectedCallType) {
      setSelectedCallType(newSelection);
    }
  };

  const handleCallReasonChange = (
    newSelection: string,
    selectedCallReason: string
  ) => {
    if (newSelection !== selectedCallReason) {
      setSelectedCallReason(newSelection);
    }
  };

  const handleScriptedChoiceChange = (event: SelectChangeEvent) => {
    setScriptedChoice(event.target.value);
  };

  useEffect(() => {
    if (
      rangeStartDate &&
      rangeEndDate &&
      rangeStartDate.isAfter(rangeEndDate, "day")
    ) {
      setRangeStartDate(rangeEndDate);
    }
  }, [rangeStartDate, rangeEndDate]);

  // Set screenshot data for send report button
  const [screenshot, setScreenshot] = useState<any>();
  const [isImageOpen, setIsImageOpen] = useState<boolean>(false);

  const onButtonClick = async () => {
    const canvas = await html2canvas(document.body);
    const dataUrl = canvas.toDataURL("image/png");

    setScreenshot(dataUrl);
  };

  // Handle click for alerts
  const handleLabelClick = (labelIndex: any) => {
    // if (labelIndex === 0) {
    //     console.log(alertData.Key_alerts[0].Key_metric)
    //     setSelectedCallCenter(alertData.key_alerts[0].cc_id);
    //     setSelectedRadio(alertData.key_alerts[0].key_metric[0]);
    // } else if (labelIndex === 1) {
    //     console.log(alertData.Key_alerts[1].Key_metric)
    //     setSelectedCallCenter(alertData.key_alerts[1].cc_id);
    //     setSelectedRadio(alertData.key_alerts[1].key_metric[0]);
    // }
    setClickedLabel(labelIndex);
  };

  // Updated DatePicker onChange handler
  const handleEndDateChange = (
    selectedOption: string,
    newDate: dayjs.Dayjs
  ) => {
    // Calculate updatedStartDate asynchronously
    setEndDate(newDate);
    let calculatedStartDate;

    setClickedLabel(null);

    const calculateUpdatedStartDate = () => {
      calculatedStartDate = newDate;

      if (selectedOption === "Last 7 days" && newDate instanceof dayjs) {
        calculatedStartDate = newDate.subtract(6, "days");
      } else if (
        selectedOption === "Last 30 days" &&
        newDate instanceof dayjs
      ) {
        calculatedStartDate = newDate.subtract(30, "days");
      } else if (
        selectedOption === "Last 60 days" &&
        newDate instanceof dayjs
      ) {
        calculatedStartDate = newDate.subtract(60, "days");
      }
      setStartDate(calculatedStartDate);
    };
    calculateUpdatedStartDate();
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRadio(e.target.value);
    setClickedLabel(null);
  };

  useEffect(() => {
    const url = "/top_agents";

    const fetchData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        setAgentList(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData(url);
  }, []);

  // Get compliance overview data
  useEffect(() => {
    const url = `/metrics_percentages`;

    const fetchData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        setComplianceData(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData(url);
  }, []);

  // Get compliance sentiment and resol rate (right-hand side) data
  useEffect(() => {
    Promise.all([
      fetch(`/prohibited_phrase_sentiment_resolution`),
      fetch(`/missed_actions_sentiment_resolution`),
      fetch(`/caller_authentication_sentiment_resolution`),
      fetch(`/scripted_opening_sentiment_resolution`),
      fetch(`/scripted_closing_sentiment_resolution`),
      fetch(`/call_theme_disclosure_sentiment_resolution`),
      fetch(`/call_recording_disclosure_sentiment_resolution`),
      fetch(`/call_recording_failure_consent_sentiment_resolution`),
      fetch(`/call_theme_disclosure_failure_consent_sentiment_resolution`),
    ])
      .then(
        ([
          metricsOne,
          metricsTwo,
          metricsThree,
          metricsFour,
          metricsFive,
          metricsSix,
          metricsSeven,
          metricsEight,
          metricsNine,
        ]) =>
          Promise.all([
            metricsOne.json(),
            metricsTwo.json(),
            metricsThree.json(),
            metricsFour.json(),
            metricsFive.json(),
            metricsSix.json(),
            metricsSeven.json(),
            metricsEight.json(),
            metricsNine.json(),
          ])
      )
      .then(
        ([
          prohibitedData,
          missedData,
          callerData,
          openingData,
          closingData,
          themeData,
          recordData,
          recFailureData,
          themeFailureData,
        ]) => {
          setSentimentResolData({
            prohibitedData,
            missedData,
            callerData,
            openingData,
            closingData,
            themeData,
            recordData,
            recFailureData,
            themeFailureData,
          });
        }
      );
  }, []);

  // Get compliance metrics data
  useEffect(() => {
    Promise.all([
      fetch(`/metrics_prohibited_phrase`),
      fetch(`/metrics_missed_actions`),
      fetch(`/metrics_caller_authentication`),
      fetch(`/metrics_scripted_opening`),
      fetch(`/metrics_scripted_closing`),
      fetch(`/metrics_call_theme_disclosure`),
      fetch(`/metrics_call_recording_disclosure`),
      fetch(`/metrics_call_recording_failure_consent`),
      fetch(`/metrics_call_theme_disclosure_failure_consent`),
    ])
      .then(
        ([
          metricsOne,
          metricsTwo,
          metricsThree,
          metricsFour,
          metricsFive,
          metricsSix,
          metricsSeven,
          metricsEight,
          metricsNine,
        ]) =>
          Promise.all([
            metricsOne.json(),
            metricsTwo.json(),
            metricsThree.json(),
            metricsFour.json(),
            metricsFive.json(),
            metricsSix.json(),
            metricsSeven.json(),
            metricsEight.json(),
            metricsNine.json(),
          ])
      )
      .then(
        ([
          prohibitedData,
          missedData,
          callerData,
          openingData,
          closingData,
          themeData,
          recordData,
          recFailureData,
          themeFailureData,
        ]) => {
          setMetricsData({
            prohibitedData,
            missedData,
            callerData,
            openingData,
            closingData,
            themeData,
            recordData,
            recFailureData,
            themeFailureData,
          });
        }
      );
  }, []);

  // Get agent details
  useEffect(() => {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const url = `/agent_report?start_date=${formattedStartDate}&end_date=${formattedEndDate}&agent_id=${selectedAgentId}`;

    const fetchData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        setAgentData(result.metrics);
        setAgentTenure(extractDuration(result.metrics.agent_tenure));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData(url);
  }, [startDate, endDate, selectedAgentId]);

  // Get graph data
  useEffect(() => {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const url = `/get_graph?start_date=${formattedStartDate}&end_date=${formattedEndDate}&agent_id=${selectedAgentId}&metric=${scriptedChoice}`;

    const fetchData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        // console.log(result)
        const agentAvgArray = result.graph_values.map(
          (item: any) => item.agent_avg
        );
        const dateArray = result.graph_values.map((item: any) =>
          new Date(item.date).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
          })
        );
        const peerAvgArray = result.graph_values.map(
          (item: any) => item.peer_avg
        );
        const topAvgArray = result.graph_values.map(
          (item: any) => item.top_average
        );

        setGraphData([agentAvgArray, dateArray, peerAvgArray, topAvgArray]);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData(url);
  }, [startDate, endDate, selectedAgentId, scriptedChoice]);

  // Get agent resol percentage and sentiment score data
  useEffect(() => {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    let formattedCallType = "";
    let formattedCallReason = "";

    if (selectedCallType === "All") {
      formattedCallType = "";
    } else {
      formattedCallType = selectedCallType;
    }

    if (selectedCallReason === "All") {
      formattedCallReason = "";
    } else {
      formattedCallReason = selectedCallReason;
    }

    const url = `/sentiment_resolution_percentage?agent_id=${selectedAgentId}&call_type=${formattedCallType}&call_reason=${formattedCallReason}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;

    const fetchData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        // console.log(result);

        // Check if the status is 1 (data available) or 0 (no data available)
        if (result.status === 1) {
          setAgentSentimentResolData(result.values);
        } else {
          setAgentSentimentResolData(null);
        }
      } catch (error) {
        setAgentSentimentResolData(null);
        console.error("Error fetching data:", error);
      }
    };
    fetchData(url);
  }, [
    startDate,
    endDate,
    selectedAgentId,
    selectedCallType,
    selectedCallReason,
  ]);

  // Get agent mortgage loan promotion missed opportunities transcript data
  useEffect(() => {
    const url = `/fetch_transcripts_new?agent_id=${selectedAgentId}`;

    const fetchData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        // console.log(result);
        setAgentPromotionTranscriptData(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData(url);
  }, [selectedAgentId]);

  // Get agent good transcript data
  useEffect(() => {
    const url = `/fetch_transcripts?agent_id=${selectedAgentId}&&nature=1`;

    const fetchData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        // console.log(result);
        setAgentGoodTranscriptData(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData(url);
  }, [selectedAgentId]);

  // Get agent bad transcript data
  useEffect(() => {
    const url = `/fetch_transcripts?agent_id=${selectedAgentId}&&nature=0`;

    const fetchData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        // console.log(result);
        setAgentBadTranscriptData(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData(url);
  }, [selectedAgentId]);

  // Get agent feedback data
  useEffect(() => {
    setIsFeedbackLoading(true);
    let controller = new AbortController();
    let signal = controller.signal;

    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const url = "/generate_feedback";

    const fetchData = async () => {
      try {
        const response = await fetch(url, {
          signal,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            agent_id: selectedAgentId,
          }),
        });
        const result = await response.json();
        // console.log(result)
        setFeedbackData(result.feedback);
        setIsFeedbackLoading(false);
      } catch (error: any) {
        if (error.name === "AbortError") {
          // console.log('Request aborted');
        } else {
          console.error("Error fetching data:", error);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
      controller = new AbortController();
    };
  }, [startDate, endDate, selectedAgentId]);

  // Get alerts data via proxy
  useEffect(() => {
    setIsLoading(true);
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    const fetchData = async () => {
      try {
        const response = await fetch(
          `/disclosure_alerts?start_date=${formattedStartDate}&end_date=${formattedEndDate}`
        );
        const result = await response.json();
        // console.log(result)
        setAlertData(result);
        try {
          const array = result.disclosure_alerts.slice(1, -1).split("', '");
          setAlertText([
            array[0].replace(/'/g, ""),
            array[1].replace(/'/g, ""),
          ]);
        } catch (e) {
          const lines = result.disclosure_alerts.split("\n");
          const array = lines.map((line: any) => line.match(/'(.*?)'/)[1]);
          setAlertText([array[0], array[1]]);
          console.error("Error parsing disclosure alert data:", e);
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedOption, selectedCallCenter, startDate, endDate]);

  const [selectedTranscriptDetails, setSelectedTranscriptDetails] =
    useState<any>([]);
  const [selectedTranscript, setSelectedTranscript] = useState<[string, any[]]>(
    ["", []]
  );
  const [highlightsData, setHighlightsData] = useState<any>([]);
  const [open, setOpen] = useState<boolean>(false);

  const getAgentTranscriptData = async (transcript_id: string) => {
    try {
      const response = await fetch(
        `/fetch_transcript_dislosure_agent?transcript_id=${transcript_id}`
      );
      const result = await response.json();
      // console.log(result)
      handleOpen(result[0]);
    } catch (error: any) {
      console.error("Error fetching agent transcript data:", error);
    }
  };

  const handleOpen = (item: any) => {
    // console.log(item);

    let cleanTranscript = item["transcript"].replace(/\*/g, "");

    // console.log(cleanTranscript)

    let agentIndex = cleanTranscript.indexOf("Agent:");

    let finalTranscript;
    if (agentIndex !== -1) {
      finalTranscript = cleanTranscript.substring(agentIndex);
    } else {
      finalTranscript = cleanTranscript;
    }

    const transcriptArray = finalTranscript
      .split(/(?=Agent(\s[A-Za-z]+)?:|Customer:)/)
      .filter(
        (segment: string) =>
          segment !== undefined && segment.trim().split(/\s+/).length > 1
      ) // Filter out undefined segments
      .map((segment: string, index: number) => {
        if (index % 2 === 0) {
          return segment.trim();
        } else {
          return `\n${segment}`;
        }
      });

    // Remove the first element of the array if it starts with '[Agent]'
    if (transcriptArray[0] === "[Agent]") {
      transcriptArray.shift();
    }

    setSelectedTranscript([item["transcript_id"], transcriptArray]);

    // Set highlights data
    const openingClosingArray = [];
    const prohibitedArray = [];
    const missedActionArray = [];
    const callerArray = [];
    const callThemeArray = [];
    const callRecordArray = [];

    setSelectedTranscriptDetails([
      item["Reason"],
      item["Call_Sentiment"],
      item["Resolved"],
      item["date"],
    ]);

    item["Scripted_Opening_Sentence"] !== "NULL"
      ? openingClosingArray.push(item["Scripted_Opening_Sentence"])
      : openingClosingArray.push("");
    item["Scripted_Closing_Sentence"] !== "NULL"
      ? openingClosingArray.push(item["Scripted_Closing_Sentence"])
      : openingClosingArray.push("");
    item["Prohibited_Phrase_Sentence"] !== "NULL"
      ? prohibitedArray.push(item["Prohibited_Phrase_Sentence"])
      : prohibitedArray.push("");
    item["Missed_Actions_Sentence"] !== "NULL"
      ? missedActionArray.push(item["Missed_Actions_Sentence"])
      : missedActionArray.push("");
    item["Caller_Authentication_Sentence"] !== "NULL"
      ? callerArray.push(item["Caller_Authentication_Sentence"])
      : callerArray.push("");
    item["Call_Theme_Specific_Disclosure_Sentence"] !== "NULL"
      ? callThemeArray.push(item["Call_Theme_Specific_Disclosure_Sentence"])
      : callThemeArray.push("");
    item["Call_Theme_Disclosure_Failure_to_Obtain_Consent_Sentence"] !== "NULL"
      ? callThemeArray.push(
          item["Call_Theme_Disclosure_Failure_to_Obtain_Consent_Sentence"]
        )
      : callThemeArray.push("");
    item["Call_Recording_Disclosure_Sentence"] !== "NULL"
      ? callRecordArray.push(item["Call_Recording_Disclosure_Sentence"])
      : callRecordArray.push("");
    item["Call_Recording_Failure_to_Obtain_Consent_Sentence"] !== "NULL"
      ? callRecordArray.push(
          item["Call_Recording_Failure_to_Obtain_Consent_Sentence"]
        )
      : callRecordArray.push("");

    setHighlightsData([
      openingClosingArray,
      prohibitedArray,
      missedActionArray,
      callerArray,
      callThemeArray,
      callRecordArray,
    ]);

    if (
      item["promotion_applicable"] &&
      item["promotion_applicable"] === "YES" &&
      item["promotion_mentioned"] === "NO"
    ) {
      setPromoSuggestion(item["promotion_feedback"]);
    } else {
      setPromoSuggestion("The promotion does not apply to this call.");
    }

    setOpen(true);
  };

  const Highlight = ({
    children,
    highlightIndex,
  }: {
    children: React.ReactNode;
    highlightIndex: number;
  }) => {
    const colors = [
      "#ffe404", // Opening and Closing
      "#ff9a91", // Prohibited_Phrase_Sentence #c8b424
      "#b7ad6c", // Missed_Actions_Sentence
      "#907c1c", // Caller_Authentication_Sentence
      "#c4c4cd", // Call_Theme_Specific_Disclosure_Sentence
      mode === "light" ? "#2e2e38" : "white", // Call_Recording_Disclosure_Sentence
    ];
    const fontColors = [
      "black", // Opening and Closing
      "black", // Prohibited_Phrase_Sentence
      "white", // Missed_Actions_Sentence
      "white", // Caller_Authentication_Sentence
      "black", // Call_Theme_Specific_Disclosure_Sentence
      mode === "light" ? "white" : "black", // Call_Recording_Disclosure_Sentence
    ];
    const color = colors[highlightIndex % colors.length];
    const fontColor = fontColors[highlightIndex % fontColors.length];
    return (
      <span
        style={{
          backgroundColor: color,
          fontFamily: theme.fonts.primary,
          color: fontColor,
        }}
      >
        {children}
      </span>
    );
  };

  const handleSendReport = () => {
    //console.log('Sending report:', { email, comments });
    setIsModalOpen(false);
  };

  // const handleAgentTranscriptClose = () => {
  //     setAgentTranscriptOpen(false);
  // };

  const handleClose = () => {
    setOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openImage = () => {
    setIsImageOpen(true);
  };

  const closeImage = () => {
    setIsImageOpen(false);
  };

  const handleReset = () => {
    setSelectedRadio("total_call_volume");
    // setNumbersAsInt(0);
    setSelectedOption("Last 7 days");
    setEndDate(dayjs("2024-01-31"));
    setStartDate(dayjs("2024-01-24"));
    setAlertData(null);
    setAlertText([]);
    setIsLoading(true);
    setClickedLabel(null);
    setSelectedCallCenter("CC001");
    setScriptedChoice("Scripted_Opening_Exists");
    setSelectedAgentId("FL3PJ06");
    setLayout("overview");
    setSelectedCallType("All");
    setSelectedCallReason("All");
    setExpandClicked(false);
  };

  return (
    <FadeIn transitionDuration={700}>
      <div className='ops-root container'>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FadeIn transitionDuration={700}>
                <Item className='ops-navbar-filters'>
                  <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>
                        <FormControl fullWidth>
                          <InputLabel id='select-label'>
                            Reference Period
                          </InputLabel>
                          <Select
                            labelId='select-label'
                            id='select'
                            value={selectedOption}
                            onChange={(e) => {
                              setSelectedOption(e.target.value);
                              handleEndDateChange(e.target.value, endDate);
                            }}
                          >
                            <MenuItem value='Last 7 days'>Last 7 days</MenuItem>
                            <MenuItem value='Last 30 days'>
                              Last 30 days
                            </MenuItem>
                            <MenuItem value='Last 60 days'>
                              Last 60 days
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale='en'
                        >
                          <div>
                            <DatePicker
                              defaultValue={dayjs("2024-01-31")}
                              label='Report Date'
                              value={endDate}
                              onChange={(e) => {
                                handleEndDateChange(
                                  selectedOption,
                                  e || dayjs("2024-01-31")
                                );
                              }}
                            />
                          </div>
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} sm={12} md={5} lg={2.8} xl={2.8}>
                        <div className='exec-buttons'>
                          <Button
                            className='ops-nav-btn exec-nav-btn'
                            variant='contained'
                            endIcon={<RestartAltIcon />}
                            onClick={handleReset}
                          >
                            Reset Filters
                          </Button>
                          <Button
                            className='ops-nav-btn exec-nav-btn'
                            variant='contained'
                            endIcon={<SendIcon />}
                            onClick={() => {
                              onButtonClick();
                              openModal();
                            }}
                          >
                            Send Report
                          </Button>
                        </div>
                      </Grid>

                      <Grid
                        className='switch-toggle'
                        item
                        xs={12}
                        sm={12}
                        md={6.2}
                        lg={6.2}
                        xl={6.2}
                      >
                        <ToggleButtonGroup
                          value={layout}
                          exclusive
                          onChange={handleLayout}
                          aria-label='layout toggle button group'
                          className='layout-toggle'
                        >
                          <ToggleButton
                            value='overview'
                            aria-label='overview layout'
                          >
                            <PeopleIcon />
                            <span>Executive</span>
                          </ToggleButton>
                          <ToggleButton value='agent' aria-label='agent layout'>
                            <PersonIcon />
                            <span>Agent</span>
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>

                      <Modal
                        className='ops-send-modal modal-global'
                        open={isModalOpen}
                        onClose={closeModal}
                        aria-labelledby='modal-modal-title'
                        aria-describedby='modal-modal-description'
                      >
                        <Box className='modal-style' sx={ModalStyle}>
                          <h3>
                            <SendIcon /> Report Form
                          </h3>
                          <div>
                            <TextField
                              label='Subject'
                              variant='outlined'
                              fullWidth
                              value={subject}
                              onChange={(e) => setSubject(e.target.value)}
                              margin='normal'
                            />
                            <TextField
                              label='Email'
                              variant='outlined'
                              fullWidth
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              margin='normal'
                            />
                            <TextField
                              label='Additional Comments'
                              variant='outlined'
                              fullWidth
                              multiline
                              rows={4}
                              value={comments}
                              onChange={(e) => setComments(e.target.value)}
                              margin='normal'
                            />
                            <FormControl
                              fullWidth
                              className='send-report-dropdown'
                            >
                              <InputLabel id='select-label'>
                                Report Frequency
                              </InputLabel>
                              <Select
                                labelId='select-label'
                                id='select'
                                value={frequency}
                                onChange={(e) => {
                                  setFrequency(e.target.value);
                                }}
                              >
                                <MenuItem value='Daily'>Daily</MenuItem>
                                <MenuItem value='Weekly'>Weekly</MenuItem>
                                <MenuItem value='Monthly'>Monthly</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              label='Num of Recurrences'
                              variant='outlined'
                              fullWidth
                              type='number'
                              value={recurrences}
                              onChange={(e) => {
                                const value = parseInt(e.target.value);
                                if (
                                  !isNaN(value) &&
                                  value >= 0 &&
                                  value <= 365
                                ) {
                                  setRecurrences(value.toString());
                                } else if (e.target.value === "") {
                                  setRecurrences("");
                                }
                              }}
                              margin='normal'
                              inputProps={{ inputMode: "numeric", max: 365 }}
                            />
                            <p className='text-dark-mode'>Recurrence Range:</p>
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                              adapterLocale='en'
                            >
                              <div className='send-report-datepickers'>
                                <DatePicker
                                  className='exec-date-picker'
                                  label='Start'
                                  value={rangeStartDate}
                                  onChange={(date) => {
                                    if (date && date.isBefore(Today, "day")) {
                                      setRangeStartDate(Today);
                                    } else {
                                      setRangeStartDate(date || Today);
                                    }
                                  }}
                                />
                                <span className='text-dark-mode'> to </span>
                                <DatePicker
                                  className='exec-date-picker'
                                  label='End'
                                  value={rangeEndDate}
                                  onChange={(date) => {
                                    if (date && date.isBefore(Today, "day")) {
                                      setRangeEndDate(Today);
                                    } else if (
                                      rangeStartDate &&
                                      date &&
                                      date.isBefore(rangeStartDate, "day")
                                    ) {
                                      setRangeEndDate(rangeStartDate);
                                    } else {
                                      setRangeEndDate(date || Today);
                                    }
                                  }}
                                />
                              </div>
                            </LocalizationProvider>
                            {screenshot && (
                              <div className='send-report-attachment'>
                                <p>Attachment:</p>
                                <img
                                  src={screenshot}
                                  alt='Screenshot'
                                  onClick={openImage}
                                />
                              </div>
                            )}
                          </div>
                          <div>
                            <Button
                              className='ops-send-btn'
                              variant='contained'
                              onClick={handleSendReport}
                            >
                              Send
                            </Button>
                          </div>
                        </Box>
                      </Modal>
                      <Modal
                        className='exec-send-modal modal-global'
                        open={isImageOpen}
                        onClose={closeImage}
                        aria-labelledby='modal-modal-title'
                        aria-describedby='modal-modal-description'
                      >
                        <Box className='modal-style' sx={ImageStyle}>
                          <div className='magnified-content'>
                            <img src={screenshot} alt='Magnified Screenshot' />
                          </div>
                        </Box>
                      </Modal>
                    </Grid>
                  </Box>
                </Item>
              </FadeIn>
            </Grid>
            <Grid item xs={12}>
              {/* {layout === 'agent' && agentData && (
                            <div className="ticker-wrap">
                                <div className="ticker-heading"><NotificationImportantIcon/> Agent Insights</div>
                                <div className="ticker">
                                <div className="ticker__item">Agent {agentData.agent_fname} in 5 out of 10 calls about credit lines, agent missed sales opportunities. However, agent handled 8 out of 10 balance inquiries accurately. In 6 out of 10 payment calls, agent missed necessary disclosures. Focusing on these areas can significantly enhance agent's performance.</div>
                                </div>
                            </div>
                        )} */}
              <Item className='ops-navbar compliance-header'>
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    {isLoading ||
                    (alertData && alertData.disclosure_alerts === "") ||
                    (alertText && alertText[0] === undefined) ? (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={6}
                        xl={6}
                        className='exec-alert'
                      >
                        <Skeleton height={70} />
                        <span className='centered-text'>
                          <GridLoading
                            visible={true}
                            height='15'
                            width='15'
                            color={mode === "dark" ? "white" : "black"}
                            ariaLabel='grid-loading'
                            radius='12.5'
                            wrapperStyle={{}}
                            wrapperClass='grid-wrapper'
                          />
                          AI is generating key alerts...
                        </span>
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={6}
                        xl={6}
                        className={`exec-alert ${
                          clickedLabel === 0 ? "clicked" : "not-clicked"
                        }`}
                        onClick={() => handleLabelClick(0)}
                      >
                        <div className='alert alert--info'>
                          {mode === "dark" ? (
                            <AutoAwesomeIcon />
                          ) : (
                            <span className='ai-driven-light'>
                              <AutoAwesomeIcon />
                            </span>
                          )}
                          <div className='content'>
                            <div className='body'>
                              <TypeAnimation
                                sequence={[
                                  `${
                                    alertText[0] !== undefined
                                      ? alertText[0]
                                      : "No data available"
                                  }`,
                                ]}
                                speed={{
                                  type: "keyStrokeDelayInMs",
                                  value: 10,
                                }}
                                cursor={false}
                              />
                            </div>
                          </div>
                        </div>
                      </Grid>
                    )}
                    {isLoading ||
                    (alertData && alertData.disclosure_alerts === "") ||
                    (alertText && alertText[1] === undefined) ? (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={6}
                        xl={6}
                        className='exec-alert'
                      >
                        <Skeleton height={70} />
                        <span className='centered-text'>
                          <GridLoading
                            visible={true}
                            height='15'
                            width='15'
                            color={mode === "dark" ? "white" : "black"}
                            ariaLabel='grid-loading'
                            radius='12.5'
                            wrapperStyle={{}}
                            wrapperClass='grid-wrapper'
                          />
                          AI is generating key alerts...
                        </span>
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={6}
                        xl={6}
                        className={`exec-alert ${
                          clickedLabel === 1 ? "clicked" : "not-clicked"
                        }`}
                        onClick={() => handleLabelClick(1)}
                      >
                        <div className='alert alert--info'>
                          {mode === "dark" ? (
                            <AutoAwesomeIcon />
                          ) : (
                            <span className='ai-driven-light'>
                              <AutoAwesomeIcon />
                            </span>
                          )}
                          <div className='content'>
                            <div className='body'>
                              <TypeAnimation
                                sequence={[
                                  `${
                                    alertText[1] !== undefined
                                      ? alertText[1]
                                      : "No data available"
                                  }`,
                                ]}
                                speed={{
                                  type: "keyStrokeDelayInMs",
                                  value: 10,
                                }}
                                cursor={false}
                              />
                            </div>
                          </div>
                        </div>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Item>
            </Grid>
          </Grid>

          {layout === "overview" && (
            <FadeIn transitionDuration={700}>
              <Grid className='ops-container-wrapper' container spacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={6}>
                  <Item className='compliance-items-wrapper'>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                        <FadeIn transitionDuration={700}>
                          <React.StrictMode>
                            <DoughnutChart
                              parentToChild={{ mode, complianceData }}
                            />
                          </React.StrictMode>
                        </FadeIn>
                      </Grid>
                      <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                        <div className='ops-left-container compliance-left'>
                          <FormControl>
                            <RadioGroup
                              aria-labelledby='demo-radio-buttons-group-label'
                              defaultValue='total_call_volume'
                              name='radio-buttons-group'
                              value={selectedRadio ?? " "}
                              onChange={handleRadioChange}
                            >
                              <FadeIn transitionDuration={700}>
                                <h3>Calls Requiring Disclosure</h3>
                                <div className='compliance-group'>
                                  <div className='compliance-group-box'>
                                    <div
                                      style={{ alignItems: "center" }}
                                      className='comp-no-radio-label'
                                    >
                                      <span className='title'>
                                        Prohibited Phrase Mentioned
                                      </span>
                                    </div>
                                    <div className='exec-radio-values compliance-metrics-values'>
                                      <div className='compliance-sidebar-number'>
                                        <SlotCounter
                                          value={
                                            complianceData
                                              ? parseFloat(
                                                  complianceData[
                                                    "prohibited_phrase_yes_percentage"
                                                  ]
                                                ).toFixed(0)
                                              : 0
                                          }
                                        />
                                        <span className='percentage-sign'>
                                          %
                                        </span>
                                      </div>
                                      <span className='exec-sidebar-percentage'>
                                        <span className='red-arrow'>2% ⬆</span>
                                      </span>
                                      <br />
                                      <span className='exec-sidebar-comment'>
                                        vs. previous 7 days
                                      </span>
                                    </div>
                                  </div>
                                  <div className='compliance-group-box'>
                                    <div
                                      style={{ alignItems: "center" }}
                                      className='comp-no-radio-label'
                                    >
                                      <span className='title'>
                                        Missed Actioning
                                      </span>
                                    </div>
                                    <div className='exec-radio-values compliance-metrics-values'>
                                      <div className='compliance-sidebar-number'>
                                        <SlotCounter
                                          value={
                                            complianceData
                                              ? parseFloat(
                                                  complianceData[
                                                    "missed_actions_yes_percentage"
                                                  ]
                                                ).toFixed(0)
                                              : 0
                                          }
                                        />
                                        <span className='percentage-sign'>
                                          %
                                        </span>
                                      </div>
                                      <span className='exec-sidebar-percentage'>
                                        <span className='red-arrow'>8% ⬆</span>
                                      </span>
                                      <br />
                                      <span className='exec-sidebar-comment'>
                                        vs. previous 7 days
                                      </span>
                                    </div>
                                  </div>
                                  <div className='compliance-group-box'>
                                    <div
                                      style={{ alignItems: "center" }}
                                      className='comp-no-radio-label'
                                    >
                                      <span className='title'>
                                        No Caller Authentication
                                      </span>
                                    </div>
                                    <div className='exec-radio-values compliance-metrics-values'>
                                      <div className='compliance-sidebar-number'>
                                        <SlotCounter
                                          value={
                                            complianceData
                                              ? parseFloat(
                                                  complianceData[
                                                    "caller_authentication_no_percentage"
                                                  ]
                                                ).toFixed(0)
                                              : 0
                                          }
                                        />
                                        <span className='percentage-sign'>
                                          %
                                        </span>
                                      </div>
                                      <span className='exec-sidebar-percentage'>
                                        <span className='green-arrow'>
                                          2% ⬇
                                        </span>
                                      </span>
                                      <br />
                                      <span className='exec-sidebar-comment'>
                                        vs. previous 7 days
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <Divider className='ops-divider compliance-hr' />

                                <div className='compliance-group'>
                                  <div className='compliance-group-box'>
                                    <div
                                      style={{ alignItems: "center" }}
                                      className='comp-no-radio-label'
                                    >
                                      <span className='title'>
                                        Scripted Opening Missing
                                      </span>
                                    </div>
                                    <div className='exec-radio-values compliance-metrics-values'>
                                      <div className='compliance-sidebar-number'>
                                        <SlotCounter
                                          value={
                                            complianceData
                                              ? parseFloat(
                                                  complianceData[
                                                    "scripted_opening_no_percentage"
                                                  ]
                                                ).toFixed(0)
                                              : 0
                                          }
                                        />
                                        <span className='percentage-sign'>
                                          %
                                        </span>
                                      </div>
                                      <span className='exec-sidebar-percentage'>
                                        <span className='red-arrow'>8% ⬆</span>
                                      </span>
                                      <br />
                                      <span className='exec-sidebar-comment'>
                                        vs. previous 7 days
                                      </span>
                                    </div>
                                  </div>
                                  <div className='compliance-group-box'>
                                    <div
                                      style={{ alignItems: "center" }}
                                      className='comp-no-radio-label'
                                    >
                                      <span className='title'>
                                        Scripted Closing Missing
                                      </span>
                                    </div>
                                    <div className='exec-radio-values compliance-metrics-values'>
                                      <div className='compliance-sidebar-number'>
                                        <SlotCounter
                                          value={
                                            complianceData
                                              ? parseFloat(
                                                  complianceData[
                                                    "scripted_closing_no_percentage"
                                                  ]
                                                ).toFixed(0)
                                              : 0
                                          }
                                        />
                                        <span className='percentage-sign'>
                                          %
                                        </span>
                                      </div>
                                      <span className='exec-sidebar-percentage'>
                                        <span className='red-arrow'>8% ⬆</span>
                                      </span>
                                      <br />
                                      <span className='exec-sidebar-comment'>
                                        vs. previous 7 days
                                      </span>
                                    </div>
                                  </div>
                                  <div className='compliance-group-box'>
                                    <div
                                      style={{ alignItems: "center" }}
                                      className='comp-no-radio-label'
                                    >
                                      <span className='title'>
                                        Call Theme Disclosure Missing
                                      </span>
                                    </div>
                                    <div className='exec-radio-values compliance-metrics-values'>
                                      <div className='compliance-sidebar-number'>
                                        <SlotCounter
                                          value={
                                            complianceData
                                              ? parseFloat(
                                                  complianceData[
                                                    "call_theme_specific_disclosure_no_percentage"
                                                  ]
                                                ).toFixed(0)
                                              : 0
                                          }
                                        />
                                        <span className='percentage-sign'>
                                          %
                                        </span>
                                      </div>
                                      <span className='exec-sidebar-percentage'>
                                        <span className='green-arrow'>
                                          2% ⬇
                                        </span>
                                      </span>
                                      <br />
                                      <span className='exec-sidebar-comment'>
                                        vs. previous 7 days
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <Divider className='ops-divider compliance-hr' />

                                <div className='compliance-group'>
                                  <div className='compliance-group-box'>
                                    <div
                                      style={{ alignItems: "center" }}
                                      className='comp-no-radio-label'
                                    >
                                      <span className='title'>
                                        No Call Recording Disclosure
                                      </span>
                                    </div>
                                    <div className='exec-radio-values compliance-metrics-values'>
                                      <div className='compliance-sidebar-number'>
                                        <SlotCounter
                                          value={
                                            complianceData
                                              ? parseFloat(
                                                  complianceData[
                                                    "call_recording_disclosure_no_percentage"
                                                  ]
                                                ).toFixed(0)
                                              : 0
                                          }
                                        />
                                        <span className='percentage-sign'>
                                          %
                                        </span>
                                      </div>
                                      <span className='exec-sidebar-percentage'>
                                        <span className='red-arrow'>2% ⬆</span>
                                      </span>
                                      <br />
                                      <span className='exec-sidebar-comment'>
                                        vs. previous 7 days
                                      </span>
                                    </div>
                                  </div>
                                  <div className='compliance-group-box'>
                                    <div
                                      style={{ alignItems: "center" }}
                                      className='comp-no-radio-label'
                                    >
                                      <span className='title'>
                                        Call Recording Failure for Consent
                                      </span>
                                    </div>
                                    <div className='exec-radio-values compliance-metrics-values'>
                                      <div className='compliance-sidebar-number'>
                                        <SlotCounter
                                          value={
                                            complianceData
                                              ? parseFloat(
                                                  complianceData[
                                                    "call_recording_failure_to_obtain_consent_yes_percentage"
                                                  ]
                                                ).toFixed(0)
                                              : 0
                                          }
                                        />
                                        <span className='percentage-sign'>
                                          %
                                        </span>
                                      </div>
                                      <span className='exec-sidebar-percentage'>
                                        <span className='red-arrow'>
                                          0.4% ⬆
                                        </span>
                                      </span>
                                      <br />
                                      <span className='exec-sidebar-comment'>
                                        vs. previous 7 days
                                      </span>
                                    </div>
                                  </div>
                                  <div className='compliance-group-box'>
                                    <div
                                      style={{ alignItems: "center" }}
                                      className='comp-no-radio-label'
                                    >
                                      <span className='title'>
                                        Call Theme Disclosure Failure for
                                        Consent
                                      </span>
                                    </div>
                                    <div className='exec-radio-values compliance-metrics-values'>
                                      <div className='compliance-sidebar-number'>
                                        <SlotCounter
                                          value={
                                            complianceData
                                              ? parseFloat(
                                                  complianceData[
                                                    "call_theme_disclosure_failure_to_obtain_consent_yes_percentage"
                                                  ]
                                                ).toFixed(0)
                                              : 0
                                          }
                                        />
                                        <span className='percentage-sign'>
                                          %
                                        </span>
                                      </div>
                                      <span className='exec-sidebar-percentage'>
                                        <span className='red-arrow'>
                                          0.2% ⬆
                                        </span>
                                      </span>
                                      <br />
                                      <span className='exec-sidebar-comment'>
                                        vs. previous 7 days
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </FadeIn>
                            </RadioGroup>
                          </FormControl>
                        </div>
                      </Grid>
                    </Grid>
                  </Item>
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={12} xl={6}>
                  <Item className='compliance-items-wrapper compliance-right compliance-metrics-scroll'>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Item className='compliance-right-element'>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
                              <h3>Prohibited Phrase Mentioned</h3>
                              <div className='compliance-group'>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Sentiment Score
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.prohibitedData
                                          ).length > 0
                                            ? parseFloat(
                                                sentimentResolData
                                                  .prohibitedData
                                                  .Average_Sentiment
                                              ).toFixed(1)
                                            : 0
                                        }
                                      />
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='green-arrow'>4% ⬆</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Resolution Rate
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.prohibitedData
                                          ).length > 0
                                            ? (
                                                parseFloat(
                                                  sentimentResolData
                                                    .prohibitedData
                                                    .Resolution_Rate
                                                ) * 100
                                              ).toFixed(0)
                                            : 0
                                        }
                                      />
                                      <span className='percentage-sign'>%</span>
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='red-arrow'>4% ⬇</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
                              <TableContainer>
                                <Table
                                  className='compliance-clickable-table'
                                  size='small'
                                  aria-label='a dense table'
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='left'
                                      >
                                        Transcript Date
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "20%" }}
                                        align='right'
                                      >
                                        Call ID
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='right'
                                      >
                                        Call Reason
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {metricsData &&
                                      Object.keys(metricsData.prohibitedData)
                                        .length > 0 &&
                                      metricsData.prohibitedData.map(
                                        (item: any, index: number) => (
                                          <TableRow
                                            key={index}
                                            component='tr'
                                            sx={{
                                              "& > *": {
                                                borderBottom: "unset",
                                              },
                                            }}
                                            onClick={() => handleOpen(item)}
                                          >
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='left'
                                            >
                                              {formatDateString(item.date)}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "20%" }}
                                              align='right'
                                            >
                                              {item.transcript_id}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='right'
                                            >
                                              {item.Reason}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        </Item>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Item className='compliance-right-element'>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
                              <h3>Missed Actioning</h3>
                              <div className='compliance-group'>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Sentiment Score
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.missedData
                                          ).length > 0
                                            ? parseFloat(
                                                sentimentResolData.missedData
                                                  .Average_Sentiment
                                              ).toFixed(1)
                                            : 0
                                        }
                                      />
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='green-arrow'>4% ⬆</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Resolution Rate
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.missedData
                                          ).length > 0
                                            ? (
                                                parseFloat(
                                                  sentimentResolData.missedData
                                                    .Resolution_Rate
                                                ) * 100
                                              ).toFixed(0)
                                            : 0
                                        }
                                      />
                                      <span className='percentage-sign'>%</span>
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='red-arrow'>4% ⬇</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
                              <TableContainer>
                                <Table
                                  className='compliance-clickable-table'
                                  size='small'
                                  aria-label='a dense table'
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='left'
                                      >
                                        Transcript Date
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "20%" }}
                                        align='right'
                                      >
                                        Call ID
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='right'
                                      >
                                        Call Reason
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {metricsData &&
                                      Object.keys(metricsData.missedData)
                                        .length > 0 &&
                                      metricsData.missedData.map(
                                        (item: any, index: number) => (
                                          <TableRow
                                            key={index}
                                            component='tr'
                                            sx={{
                                              "& > *": {
                                                borderBottom: "unset",
                                              },
                                            }}
                                            onClick={() => handleOpen(item)}
                                          >
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='left'
                                            >
                                              {formatDateString(item.date)}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "20%" }}
                                              align='right'
                                            >
                                              {item.transcript_id}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='right'
                                            >
                                              {item.Reason}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        </Item>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Item className='compliance-right-element'>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
                              <h3>No Caller Authentication</h3>
                              <div className='compliance-group'>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Sentiment Score
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.callerData
                                          ).length > 0
                                            ? parseFloat(
                                                sentimentResolData.callerData
                                                  .Average_Sentiment
                                              ).toFixed(1)
                                            : 0
                                        }
                                      />
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='green-arrow'>4% ⬆</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Resolution Rate
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.callerData
                                          ).length > 0
                                            ? (
                                                parseFloat(
                                                  sentimentResolData.callerData
                                                    .Resolution_Rate
                                                ) * 100
                                              ).toFixed(0)
                                            : 0
                                        }
                                      />
                                      <span className='percentage-sign'>%</span>
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='red-arrow'>4% ⬇</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
                              <TableContainer>
                                <Table
                                  className='compliance-clickable-table'
                                  size='small'
                                  aria-label='a dense table'
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='left'
                                      >
                                        Transcript Date
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "20%" }}
                                        align='right'
                                      >
                                        Call ID
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='right'
                                      >
                                        Call Reason
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {metricsData &&
                                      Object.keys(metricsData.callerData)
                                        .length > 0 &&
                                      metricsData.callerData.map(
                                        (item: any, index: number) => (
                                          <TableRow
                                            key={index}
                                            component='tr'
                                            sx={{
                                              "& > *": {
                                                borderBottom: "unset",
                                              },
                                            }}
                                            onClick={() => handleOpen(item)}
                                          >
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='left'
                                            >
                                              {formatDateString(item.date)}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "20%" }}
                                              align='right'
                                            >
                                              {item.transcript_id}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='right'
                                            >
                                              {item.Reason}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        </Item>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Item className='compliance-right-element'>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
                              <h3>Scripted Opening Missing</h3>
                              <div className='compliance-group'>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Sentiment Score
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.openingData
                                          ).length > 0
                                            ? parseFloat(
                                                sentimentResolData.openingData
                                                  .Average_Sentiment
                                              ).toFixed(1)
                                            : 0
                                        }
                                      />
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='green-arrow'>4% ⬆</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Resolution Rate
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.openingData
                                          ).length > 0
                                            ? (
                                                parseFloat(
                                                  sentimentResolData.openingData
                                                    .Resolution_Rate
                                                ) * 100
                                              ).toFixed(0)
                                            : 0
                                        }
                                      />
                                      <span className='percentage-sign'>%</span>
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='red-arrow'>4% ⬇</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
                              <TableContainer>
                                <Table
                                  className='compliance-clickable-table'
                                  size='small'
                                  aria-label='a dense table'
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='left'
                                      >
                                        Transcript Date
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "20%" }}
                                        align='right'
                                      >
                                        Call ID
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='right'
                                      >
                                        Call Reason
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {metricsData &&
                                      Object.keys(metricsData.openingData)
                                        .length > 0 &&
                                      metricsData.openingData.map(
                                        (item: any, index: number) => (
                                          <TableRow
                                            key={index}
                                            component='tr'
                                            sx={{
                                              "& > *": {
                                                borderBottom: "unset",
                                              },
                                            }}
                                            onClick={() => handleOpen(item)}
                                          >
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='left'
                                            >
                                              {formatDateString(item.date)}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "20%" }}
                                              align='right'
                                            >
                                              {item.transcript_id}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='right'
                                            >
                                              {item.Reason}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        </Item>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Item className='compliance-right-element'>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
                              <h3>Scripted Closing Missing</h3>
                              <div className='compliance-group'>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Sentiment Score
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.closingData
                                          ).length > 0
                                            ? parseFloat(
                                                sentimentResolData.closingData
                                                  .Average_Sentiment
                                              ).toFixed(1)
                                            : 0
                                        }
                                      />
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='green-arrow'>4% ⬆</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Resolution Rate
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.closingData
                                          ).length > 0
                                            ? (
                                                parseFloat(
                                                  sentimentResolData.closingData
                                                    .Resolution_Rate
                                                ) * 100
                                              ).toFixed(0)
                                            : 0
                                        }
                                      />
                                      <span className='percentage-sign'>%</span>
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='red-arrow'>4% ⬇</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
                              <TableContainer>
                                <Table
                                  className='compliance-clickable-table'
                                  size='small'
                                  aria-label='a dense table'
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='left'
                                      >
                                        Transcript Date
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "20%" }}
                                        align='right'
                                      >
                                        Call ID
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='right'
                                      >
                                        Call Reason
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {metricsData &&
                                      Object.keys(metricsData.closingData)
                                        .length > 0 &&
                                      metricsData.closingData.map(
                                        (item: any, index: number) => (
                                          <TableRow
                                            key={index}
                                            component='tr'
                                            sx={{
                                              "& > *": {
                                                borderBottom: "unset",
                                              },
                                            }}
                                            onClick={() => handleOpen(item)}
                                          >
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='left'
                                            >
                                              {formatDateString(item.date)}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "20%" }}
                                              align='right'
                                            >
                                              {item.transcript_id}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='right'
                                            >
                                              {item.Reason}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        </Item>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Item className='compliance-right-element'>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
                              <h3>Call Theme Disclosure Missing</h3>
                              <div className='compliance-group'>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Sentiment Score
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.themeData
                                          ).length > 0
                                            ? parseFloat(
                                                sentimentResolData.themeData
                                                  .Average_Sentiment
                                              ).toFixed(1)
                                            : 0
                                        }
                                      />
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='green-arrow'>4% ⬆</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Resolution Rate
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.themeData
                                          ).length > 0
                                            ? (
                                                parseFloat(
                                                  sentimentResolData.themeData
                                                    .Resolution_Rate
                                                ) * 100
                                              ).toFixed(0)
                                            : 0
                                        }
                                      />
                                      <span className='percentage-sign'>%</span>
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='red-arrow'>4% ⬇</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
                              <TableContainer>
                                <Table
                                  className='compliance-clickable-table'
                                  size='small'
                                  aria-label='a dense table'
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='left'
                                      >
                                        Transcript Date
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "20%" }}
                                        align='right'
                                      >
                                        Call ID
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='right'
                                      >
                                        Call Reason
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {metricsData &&
                                      Object.keys(metricsData.themeData)
                                        .length > 0 &&
                                      metricsData.themeData.map(
                                        (item: any, index: number) => (
                                          <TableRow
                                            key={index}
                                            component='tr'
                                            sx={{
                                              "& > *": {
                                                borderBottom: "unset",
                                              },
                                            }}
                                            onClick={() => handleOpen(item)}
                                          >
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='left'
                                            >
                                              {formatDateString(item.date)}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "20%" }}
                                              align='right'
                                            >
                                              {item.transcript_id}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='right'
                                            >
                                              {item.Reason}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        </Item>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Item className='compliance-right-element'>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
                              <h3>No Call Recording Disclosure</h3>
                              <div className='compliance-group'>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Sentiment Score
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.recordData
                                          ).length > 0
                                            ? parseFloat(
                                                sentimentResolData.recordData
                                                  .Average_Sentiment
                                              ).toFixed(1)
                                            : 0
                                        }
                                      />
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='green-arrow'>4% ⬆</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Resolution Rate
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.recordData
                                          ).length > 0
                                            ? (
                                                parseFloat(
                                                  sentimentResolData.recordData
                                                    .Resolution_Rate
                                                ) * 100
                                              ).toFixed(0)
                                            : 0
                                        }
                                      />
                                      <span className='percentage-sign'>%</span>
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='red-arrow'>4% ⬇</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
                              <TableContainer>
                                <Table
                                  className='compliance-clickable-table'
                                  size='small'
                                  aria-label='a dense table'
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='left'
                                      >
                                        Transcript Date
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "20%" }}
                                        align='right'
                                      >
                                        Call ID
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='right'
                                      >
                                        Call Reason
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {metricsData &&
                                      Object.keys(metricsData.recordData)
                                        .length > 0 &&
                                      metricsData.recordData.map(
                                        (item: any, index: number) => (
                                          <TableRow
                                            key={index}
                                            component='tr'
                                            sx={{
                                              "& > *": {
                                                borderBottom: "unset",
                                              },
                                            }}
                                            onClick={() => handleOpen(item)}
                                          >
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='left'
                                            >
                                              {formatDateString(item.date)}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "20%" }}
                                              align='right'
                                            >
                                              {item.transcript_id}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='right'
                                            >
                                              {item.Reason}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        </Item>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Item className='compliance-right-element'>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
                              <h3>Call Recording Failure for Consent</h3>
                              <div className='compliance-group'>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Sentiment Score
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.recFailureData
                                          ).length > 0
                                            ? parseFloat(
                                                sentimentResolData
                                                  .recFailureData
                                                  .Average_Sentiment
                                              ).toFixed(1)
                                            : 0
                                        }
                                      />
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='green-arrow'>4% ⬆</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Resolution Rate
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.recFailureData
                                          ).length > 0
                                            ? (
                                                parseFloat(
                                                  sentimentResolData
                                                    .recFailureData
                                                    .Resolution_Rate
                                                ) * 100
                                              ).toFixed(0)
                                            : 0
                                        }
                                      />
                                      <span className='percentage-sign'>%</span>
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='red-arrow'>4% ⬇</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
                              <TableContainer>
                                <Table
                                  className='compliance-clickable-table'
                                  size='small'
                                  aria-label='a dense table'
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='left'
                                      >
                                        Transcript Date
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "20%" }}
                                        align='right'
                                      >
                                        Call ID
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='right'
                                      >
                                        Call Reason
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {metricsData &&
                                      Object.keys(metricsData.recFailureData)
                                        .length > 0 &&
                                      metricsData.recFailureData.map(
                                        (item: any, index: number) => (
                                          <TableRow
                                            key={index}
                                            component='tr'
                                            sx={{
                                              "& > *": {
                                                borderBottom: "unset",
                                              },
                                            }}
                                            onClick={() => handleOpen(item)}
                                          >
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='left'
                                            >
                                              {formatDateString(item.date)}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "20%" }}
                                              align='right'
                                            >
                                              {item.transcript_id}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='right'
                                            >
                                              {item.Reason}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        </Item>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Item className='compliance-right-element'>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
                              <h3>Call Theme Disclosure Failure for Consent</h3>
                              <div className='compliance-group'>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Sentiment Score
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.themeFailureData
                                          ).length > 0
                                            ? parseFloat(
                                                sentimentResolData
                                                  .themeFailureData
                                                  .Average_Sentiment
                                              ).toFixed(1)
                                            : 0
                                        }
                                      />
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='green-arrow'>4% ⬆</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                                <div className='compliance-group-box'>
                                  <div
                                    style={{ alignItems: "center" }}
                                    className='comp-no-radio-label comp-right-label'
                                  >
                                    <span className='title'>
                                      Resolution Rate
                                    </span>
                                  </div>
                                  <div className='exec-radio-values compliance-metrics-values'>
                                    <div className='compliance-sidebar-number'>
                                      <SlotCounter
                                        value={
                                          Object.keys(
                                            sentimentResolData.themeFailureData
                                          ).length > 0
                                            ? (
                                                parseFloat(
                                                  sentimentResolData
                                                    .themeFailureData
                                                    .Resolution_Rate
                                                ) * 100
                                              ).toFixed(0)
                                            : 0
                                        }
                                      />
                                      <span className='percentage-sign'>%</span>
                                    </div>
                                    <span className='exec-sidebar-percentage'>
                                      <span className='red-arrow'>4% ⬇</span>
                                    </span>
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous 7 days
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Grid>
                            <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
                              <TableContainer>
                                <Table
                                  className='compliance-clickable-table'
                                  size='small'
                                  aria-label='a dense table'
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='left'
                                      >
                                        Transcript Date
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "20%" }}
                                        align='right'
                                      >
                                        Call ID
                                      </TableCell>
                                      <TableCell
                                        style={{ width: "40%" }}
                                        align='right'
                                      >
                                        Call Reason
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {metricsData &&
                                      Object.keys(metricsData.themeFailureData)
                                        .length > 0 &&
                                      metricsData.themeFailureData.map(
                                        (item: any, index: number) => (
                                          <TableRow
                                            key={index}
                                            component='tr'
                                            sx={{
                                              "& > *": {
                                                borderBottom: "unset",
                                              },
                                            }}
                                            onClick={() => handleOpen(item)}
                                          >
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='left'
                                            >
                                              {formatDateString(item.date)}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "20%" }}
                                              align='right'
                                            >
                                              {item.transcript_id}
                                            </TableCell>
                                            <TableCell
                                              style={{ width: "40%" }}
                                              align='right'
                                            >
                                              {item.Reason}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        </Item>
                      </Grid>
                    </Grid>
                  </Item>
                </Grid>
              </Grid>
            </FadeIn>
          )}

          {layout === "agent" && (
            <FadeIn transitionDuration={700}>
              <Grid className='ops-container-wrapper' container spacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={6}>
                  {/* <Grid container spacing={2}>
                            <Grid item xs={12}> */}
                  <Item className='compliance-items-wrapper'>
                    <Grid container spacing={2}>
                      <Grid
                        className='compliance-right-header'
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        xl={12}
                      >
                        <h3 className='disclosure_agent_left_title'>
                          Agent Performance
                        </h3>
                      </Grid>
                      <Grid
                        item
                        className='padding-top-zero'
                        xs={12}
                        sm={12}
                        md={12}
                        lg={5}
                        xl={5}
                      >
                        <div className='disclosure_agent_top_left'>
                          <FormControl
                            variant='standard'
                            sx={{ m: 1, minWidth: 120 }}
                          >
                            <InputLabel id='demo-simple-select-standard-label'>
                              Agent Name
                            </InputLabel>
                            <Select
                              labelId='demo-simple-select-standard-label'
                              id='demo-simple-select-standard'
                              value={selectedAgentId}
                              onChange={(e) => {
                                setSelectedAgentId(e.target.value);
                                handleAgentIdChange(
                                  e.target.value,
                                  selectedAgentId
                                );
                              }}
                              label='Agent'
                            >
                              {agentList &&
                                agentList.map((item) => (
                                  <MenuItem
                                    key={item["agent_id"]}
                                    value={item["agent_id"]}
                                  >
                                    {item["agent_firstname"]}{" "}
                                    {item["agent_lastname"]}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                          <div className='profile_conteiner'>
                            <div className='image_conteiner'>
                              <Avatar className='profile_avatar' />
                              <div>
                                <h2>
                                  {agentData
                                    ? agentData.agent_id
                                    : "Loading..."}
                                </h2>
                                <p className='agent_email'>
                                  {agentData
                                    ? agentData.agent_email
                                    : "Loading..."}
                                </p>
                              </div>
                            </div>
                            <fieldset className='agent_profile_border'>
                              <legend>Tenure</legend>
                            </fieldset>
                            <div className='text_conteiner'>
                              <div className='tenure_container'>
                                <span className='tenure_number'>
                                  {agentData ? agentTenure[0] : "Loading..."}
                                </span>
                                <span className='unit'>Years</span>
                              </div>
                              <div className='tenure_container'>
                                <span className='tenure_number'>
                                  {agentData ? agentTenure[1] : "Loading..."}
                                </span>
                                <span className='unit'>Months</span>
                              </div>
                              <div className='tenure_container'>
                                <span className='tenure_number'>
                                  {agentData ? agentTenure[2] : "Loading..."}
                                </span>
                                <span className='unit'>Days</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Grid>

                      <Grid
                        item
                        className='padding-top-zero'
                        xs={12}
                        sm={12}
                        md={12}
                        lg={7}
                        xl={7}
                      >
                        <div className='disclosure_agent_top_left'>
                          <FormControl
                            variant='standard'
                            sx={{ m: 1, minWidth: 120 }}
                          >
                            <InputLabel id='demo-simple-select-standard-label'>
                              Metric
                            </InputLabel>
                            <Select
                              labelId='demo-simple-select-standard-label'
                              id='demo-simple-select-standard'
                              value={scriptedChoice}
                              onChange={handleScriptedChoiceChange}
                              label='Metric'
                            >
                              <MenuItem value='Scripted_Opening_Exists'>
                                Scripted Opening
                              </MenuItem>
                              <MenuItem value='Scripted_Closing_Exists'>
                                Scripted Closing
                              </MenuItem>
                              <MenuItem value='Prohibited_Phrase_Exists'>
                                Prohibited Phrase Mention
                              </MenuItem>
                              <MenuItem value='Caller_Authentication_Exists'>
                                Caller Authentication
                              </MenuItem>
                              <MenuItem value='Call_Recording_Disclosure_Exists'>
                                Call Recording Disclosure
                              </MenuItem>
                              <MenuItem value='Call_Theme_Specific_Disclosure_Exists'>
                                Call Theme Disclosure
                              </MenuItem>
                              <MenuItem value='Missed_Actions_Exists'>
                                Missed Actioning
                              </MenuItem>
                              <MenuItem value='Call_Theme_Disclosure_Failure_to_Obtain_Consent_Exists'>
                                Call Theme Disclosure Failure for Consent
                              </MenuItem>
                              <MenuItem value='Call_Recording_Failure_to_Obtain_Consent_Exists'>
                                Call Recording Failure for Consent
                              </MenuItem>
                            </Select>
                          </FormControl>
                          <div className='compliance-chart-container'>
                            <Line options={options} data={data} />
                          </div>
                        </div>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        xl={12}
                        className='compliance-chart-wrapper'
                      >
                        <FadeIn transitionDuration={700}>
                          <TableContainer
                            className='compliance-table disclosure-agent-table'
                            component={Paper}
                          >
                            <Table size='small' aria-label='a dense table'>
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    style={{ width: "50%" }}
                                    align='left'
                                  >
                                    Metrics
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    Agent
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "25%" }}
                                    align='center'
                                  >
                                    Avg Peers
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "15%" }}
                                    align='center'
                                  >
                                    Top Decile
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    Percentile
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow
                                  sx={{ "& > *": { borderBottom: "unset" } }}
                                >
                                  <TableCell
                                    style={{ width: "50%" }}
                                    align='left'
                                  >
                                    Scripted Opening
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[0][
                                          "Scripted_Opening_Exists"
                                        ].value + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "25%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[0][
                                          "Scripted_Opening_Exists"
                                        ].avg_peers + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "15%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[0][
                                          "Scripted_Opening_Exists"
                                        ].top_average + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[0][
                                          "Scripted_Opening_Exists"
                                        ].percentile
                                      : "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow
                                  sx={{ "& > *": { borderBottom: "unset" } }}
                                >
                                  <TableCell
                                    style={{ width: "50%" }}
                                    align='left'
                                  >
                                    Scripted Closing
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[1][
                                          "Scripted_Closing_Exists"
                                        ].value + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "25%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[1][
                                          "Scripted_Closing_Exists"
                                        ].avg_peers + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "15%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[1][
                                          "Scripted_Closing_Exists"
                                        ].top_average + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[1][
                                          "Scripted_Closing_Exists"
                                        ].percentile
                                      : "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow
                                  sx={{ "& > *": { borderBottom: "unset" } }}
                                >
                                  <TableCell
                                    style={{ width: "50%" }}
                                    align='left'
                                  >
                                    Prohibited Phrase Mention
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[2][
                                          "Prohibited_Phrase_Exists"
                                        ].value + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "25%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[2][
                                          "Prohibited_Phrase_Exists"
                                        ].avg_peers + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "15%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[2][
                                          "Prohibited_Phrase_Exists"
                                        ].top_average + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[2][
                                          "Prohibited_Phrase_Exists"
                                        ].percentile
                                      : "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow
                                  sx={{ "& > *": { borderBottom: "unset" } }}
                                >
                                  <TableCell
                                    style={{ width: "50%" }}
                                    align='left'
                                  >
                                    Caller Authentication
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[3][
                                          "Caller_Authentication_Exists"
                                        ].value + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "25%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[3][
                                          "Caller_Authentication_Exists"
                                        ].avg_peers + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "15%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[3][
                                          "Caller_Authentication_Exists"
                                        ].top_average + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[3][
                                          "Caller_Authentication_Exists"
                                        ].percentile
                                      : "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow
                                  sx={{ "& > *": { borderBottom: "unset" } }}
                                >
                                  <TableCell
                                    style={{ width: "50%" }}
                                    align='left'
                                  >
                                    Call Recording Disclosure
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[4][
                                          "Call_Recording_Disclosure_Exists"
                                        ].value + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "25%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[4][
                                          "Call_Recording_Disclosure_Exists"
                                        ].avg_peers + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "15%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[4][
                                          "Call_Recording_Disclosure_Exists"
                                        ].top_average + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[4][
                                          "Call_Recording_Disclosure_Exists"
                                        ].percentile
                                      : "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow
                                  sx={{ "& > *": { borderBottom: "unset" } }}
                                >
                                  <TableCell
                                    style={{ width: "50%" }}
                                    align='left'
                                  >
                                    Call Theme Disclosure
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[5][
                                          "Call_Theme_Specific_Disclosure_Exists"
                                        ].value + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "25%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[5][
                                          "Call_Theme_Specific_Disclosure_Exists"
                                        ].avg_peers + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "15%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[5][
                                          "Call_Theme_Specific_Disclosure_Exists"
                                        ].top_average + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[5][
                                          "Call_Theme_Specific_Disclosure_Exists"
                                        ].percentile
                                      : "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow
                                  sx={{ "& > *": { borderBottom: "unset" } }}
                                >
                                  <TableCell
                                    style={{ width: "50%" }}
                                    align='left'
                                  >
                                    Missed Actioning
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[6][
                                          "Missed_Actions_Exists"
                                        ].value + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "25%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[6][
                                          "Missed_Actions_Exists"
                                        ].avg_peers + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "15%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[6][
                                          "Missed_Actions_Exists"
                                        ].top_average + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[6][
                                          "Missed_Actions_Exists"
                                        ].percentile
                                      : "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow
                                  sx={{ "& > *": { borderBottom: "unset" } }}
                                >
                                  <TableCell
                                    style={{ width: "50%" }}
                                    align='left'
                                  >
                                    Call Theme Disclosure Failure for Consent
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[7][
                                          "Call_Theme_Disclosure_Failure_to_Obtain_Consent_Exists"
                                        ].value + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "25%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[7][
                                          "Call_Theme_Disclosure_Failure_to_Obtain_Consent_Exists"
                                        ].avg_peers + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "15%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[7][
                                          "Call_Theme_Disclosure_Failure_to_Obtain_Consent_Exists"
                                        ].top_average + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[7][
                                          "Call_Theme_Disclosure_Failure_to_Obtain_Consent_Exists"
                                        ].percentile
                                      : "N/A"}
                                  </TableCell>
                                </TableRow>
                                <TableRow
                                  sx={{ "& > *": { borderBottom: "unset" } }}
                                >
                                  <TableCell
                                    style={{ width: "50%" }}
                                    align='left'
                                  >
                                    Call Recording Failure for Consent
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[8][
                                          "Call_Recording_Failure_to_Obtain_Consent_Exists"
                                        ].value + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "25%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[8][
                                          "Call_Recording_Failure_to_Obtain_Consent_Exists"
                                        ].avg_peers + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "15%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[8][
                                          "Call_Recording_Failure_to_Obtain_Consent_Exists"
                                        ].top_average + "%"
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell
                                    style={{ width: "5%" }}
                                    align='center'
                                  >
                                    {agentData
                                      ? agentData.metrics[8][
                                          "Call_Recording_Failure_to_Obtain_Consent_Exists"
                                        ].percentile
                                      : "N/A"}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </FadeIn>
                      </Grid>
                    </Grid>
                  </Item>
                  {/* </Grid>
                        </Grid>                         */}
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={12} xl={6}>
                  <Item className='compliance-items-wrapper'>
                    <Grid container spacing={2}>
                      <Grid
                        className='compliance-right-header'
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        xl={12}
                      >
                        <div>
                          <Item className='compliance-right-element disclosure_coaching_insights'>
                            <Grid container spacing={2}>
                              <Grid
                                item
                                xs={12}
                                sm={12}
                                md={12}
                                lg={12}
                                xl={12}
                              >
                                <h3>
                                  Coaching Opportunities
                                  {mode === "dark" ? (
                                    <span className='shine'>
                                      <AutoAwesomeIcon />
                                    </span>
                                  ) : (
                                    <span className='ai-driven'>
                                      <AutoAwesomeIcon />
                                    </span>
                                  )}
                                </h3>
                                <div
                                  className={`compliance-group markdown-section ${
                                    isFeedbackLoading
                                      ? "agent-markdown-loading"
                                      : ""
                                  }`}
                                >
                                  {!isFeedbackLoading &&
                                  feedbackData &&
                                  feedbackData.length > 30 ? (
                                    // <>
                                    //     <p className={`expand-agent-feedback ${expandClicked ? 'expand-clicked' : ''}`}>{feedbackData.split(/\n/).map((line: any, index: number) => <React.Fragment key={index}>{line}<br/></React.Fragment>)}</p>
                                    //     <span className='expand-readmore-btn' onClick={() => {setExpandClicked(prev => !prev)}}>{expandClicked ? <>Show Less <KeyboardDoubleArrowUpIcon/></> : <>Show More <KeyboardDoubleArrowDownIcon/></>}</span>
                                    // </>
                                    <>
                                      <Markdown
                                        className={`expand-agent-feedback ${
                                          expandClicked ? "expand-clicked" : ""
                                        }`}
                                      >
                                        {feedbackData}
                                      </Markdown>
                                      <span
                                        className='expand-readmore-btn'
                                        onClick={() => {
                                          setExpandClicked((prev) => !prev);
                                        }}
                                      >
                                        {expandClicked ? (
                                          <>
                                            Show Less{" "}
                                            <KeyboardDoubleArrowUpIcon />
                                          </>
                                        ) : (
                                          <>
                                            Show More{" "}
                                            <KeyboardDoubleArrowDownIcon />
                                          </>
                                        )}
                                      </span>
                                    </>
                                  ) : isFeedbackLoading ? (
                                    <div className='position-reletive'>
                                      <Skeleton width='100%' height='200px' />
                                      <span className='centered-text'>
                                        <GridLoading
                                          visible={true}
                                          height='15'
                                          width='15'
                                          color={
                                            mode === "dark" ? "white" : "black"
                                          }
                                          ariaLabel='grid-loading'
                                          radius='12.5'
                                          wrapperStyle={{}}
                                          wrapperClass='grid-wrapper'
                                        />
                                        AI is generating agent feedback...
                                      </span>
                                    </div>
                                  ) : (
                                    <p>No data available</p>
                                  )}
                                </div>
                              </Grid>
                            </Grid>
                          </Item>
                        </div>

                        <Item className='disclosure_agent_agent disclosure_agent_right'>
                          <div>
                            <FormControl
                              variant='standard'
                              sx={{ m: 1, minWidth: 120 }}
                            >
                              <InputLabel id='demo-simple-select-standard-label'>
                                Call Type
                              </InputLabel>
                              <Select
                                labelId='demo-simple-select-standard-label'
                                id='demo-simple-select-standard'
                                value={selectedCallType}
                                onChange={(e) => {
                                  setSelectedCallType(e.target.value);
                                  handleCallTypeChange(
                                    e.target.value,
                                    selectedCallType
                                  );
                                }}
                                label='Call Type'
                              >
                                {callTypeList &&
                                  callTypeList.map(
                                    (item: string, index: number) => (
                                      <MenuItem key={index} value={item}>
                                        {item}
                                      </MenuItem>
                                    )
                                  )}
                              </Select>
                            </FormControl>
                            <FormControl
                              variant='standard'
                              sx={{ m: 1, minWidth: 120 }}
                            >
                              <InputLabel id='demo-simple-select-standard-label'>
                                Call Reason
                              </InputLabel>
                              <Select
                                labelId='demo-simple-select-standard-label'
                                id='demo-simple-select-standard'
                                value={selectedCallReason}
                                onChange={(e) => {
                                  setSelectedCallReason(e.target.value);
                                  handleCallReasonChange(
                                    e.target.value,
                                    selectedCallReason
                                  );
                                }}
                                label='Agent'
                              >
                                {callReasonList &&
                                  callReasonList.map(
                                    (item: string, index: number) => (
                                      <MenuItem key={index} value={item}>
                                        {item}
                                      </MenuItem>
                                    )
                                  )}
                              </Select>
                            </FormControl>
                          </div>
                          <div className='agent_performance_analysis'>
                            {agentSentimentResolData ? (
                              <div className='gauges_containers'>
                                <div style={{ height: "150px" }}>
                                  <GaugeChart
                                    parentToChild={{
                                      percent:
                                        agentSentimentResolData &&
                                        agentSentimentResolData.agent_resolution_rate ===
                                          0
                                          ? 0
                                          : parseFloat(
                                              agentSentimentResolData.agent_resolution_rate.toFixed(
                                                0
                                              )
                                            ) / 100,
                                      uniqueId: ".conical-gauge-1",
                                      uniqueClass: "conical-gauge-1",
                                      uniqueLabelId: ".label-1",
                                      uniqueLabelClass: "label-1",
                                      chartTitle: "Agent Resolution",
                                      handleException:
                                        agentSentimentResolData &&
                                        agentSentimentResolData.agent_resolution_rate ===
                                          100
                                          ? "gauge-exception"
                                          : "",
                                    }}
                                  />
                                </div>
                                <div style={{ height: "150px" }}>
                                  <GaugeChart
                                    parentToChild={{
                                      percent:
                                        agentSentimentResolData &&
                                        agentSentimentResolData.avg_resolution_rate ===
                                          0
                                          ? 0
                                          : parseFloat(
                                              agentSentimentResolData.avg_resolution_rate.toFixed(
                                                0
                                              )
                                            ) / 100,
                                      uniqueId: ".conical-gauge-2",
                                      uniqueClass: "conical-gauge-2",
                                      uniqueLabelId: ".label-2",
                                      uniqueLabelClass: "label-2",
                                      chartTitle: "Avg Peers",
                                      handleException:
                                        agentSentimentResolData &&
                                        agentSentimentResolData.avg_resolution_rate ===
                                          100
                                          ? "gauge-exception"
                                          : "",
                                    }}
                                  />
                                </div>
                                <div style={{ height: "150px" }}>
                                  <GaugeChartSentiment
                                    parentToChild={{
                                      percent:
                                        agentSentimentResolData &&
                                        agentSentimentResolData.agent_sentiment ===
                                          0
                                          ? 0
                                          : parseFloat(
                                              agentSentimentResolData.agent_sentiment.toFixed(
                                                0
                                              )
                                            ) / 10,
                                      uniqueId: ".conical-gauge-3",
                                      uniqueClass: "conical-gauge-3",
                                      uniqueLabelId: ".label-3",
                                      uniqueLabelClass: "label-3",
                                      chartTitle: "Agent Sentiment",
                                      handleException:
                                        agentSentimentResolData &&
                                        agentSentimentResolData.agent_sentiment ===
                                          10
                                          ? "sentiment-gauge-exception"
                                          : "",
                                      handleExceptionUnit:
                                        agentSentimentResolData &&
                                        agentSentimentResolData.average_sentiment ===
                                          10
                                          ? "sentiment-gauge-exception-unit"
                                          : "",
                                    }}
                                  />
                                </div>
                                <div style={{ height: "150px" }}>
                                  <GaugeChartSentiment
                                    parentToChild={{
                                      percent:
                                        agentSentimentResolData &&
                                        agentSentimentResolData.average_sentiment ===
                                          0
                                          ? 0
                                          : parseFloat(
                                              agentSentimentResolData.average_sentiment.toFixed(
                                                0
                                              )
                                            ) / 10,
                                      uniqueId: ".conical-gauge-4",
                                      uniqueClass: "conical-gauge-4",
                                      uniqueLabelId: ".label-4",
                                      uniqueLabelClass: "label-4",
                                      chartTitle: "Avg Sentiment",
                                      handleException:
                                        agentSentimentResolData &&
                                        agentSentimentResolData.average_sentiment ===
                                          10
                                          ? "sentiment-gauge-exception"
                                          : "",
                                      handleExceptionUnit:
                                        agentSentimentResolData &&
                                        agentSentimentResolData.average_sentiment ===
                                          10
                                          ? "sentiment-gauge-exception-unit"
                                          : "",
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <p className='agent_sentiment_resol_error'>
                                No data is available for the selected filters
                              </p>
                            )}
                          </div>
                        </Item>

                        <div className='agent_performance_insights'>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                              <Item className='compliance-right-element disclosure_agent_bottom'>
                                <p className='agent_sample_transcripts_info'>
                                  <InfoIcon /> Click to view the sample
                                  transcripts
                                </p>
                                <Grid container spacing={2}>
                                  <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={4}
                                    lg={4}
                                    xl={4}
                                  >
                                    <div className='disclosure_agent_sample_transcripts'>
                                      <p>
                                        Missed Opportunities
                                        {mode === "dark" ? (
                                          <span className='shine'>
                                            <AutoAwesomeIcon />
                                          </span>
                                        ) : (
                                          <span className='ai-driven'>
                                            <AutoAwesomeIcon />
                                          </span>
                                        )}
                                      </p>
                                      <TableContainer>
                                        <Table
                                          className='compliance-clickable-table'
                                          size='small'
                                          aria-label='a dense table'
                                        >
                                          <TableHead>
                                            <TableRow>
                                              <TableCell
                                                style={{ width: "100%" }}
                                                align='center'
                                              >
                                                Transcript ID
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {agentPromotionTranscriptData &&
                                              agentPromotionTranscriptData.length >
                                                0 &&
                                              agentPromotionTranscriptData.map(
                                                (item: any, index: number) => (
                                                  <TableRow
                                                    key={index}
                                                    component='tr'
                                                    sx={{
                                                      "& > *": {
                                                        borderBottom: "unset",
                                                      },
                                                    }}
                                                    onClick={() => {
                                                      getAgentTranscriptData(
                                                        item.transcript_id
                                                      );
                                                    }}
                                                  >
                                                    <TableCell
                                                      style={{ width: "100%" }}
                                                      align='center'
                                                    >
                                                      {item.transcript_id}
                                                    </TableCell>
                                                  </TableRow>
                                                )
                                              )}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </div>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={4}
                                    lg={4}
                                    xl={4}
                                  >
                                    <div className='disclosure_agent_sample_transcripts'>
                                      <p>
                                        Agent Successes
                                        {mode === "dark" ? (
                                          <span className='shine'>
                                            <AutoAwesomeIcon />
                                          </span>
                                        ) : (
                                          <span className='ai-driven'>
                                            <AutoAwesomeIcon />
                                          </span>
                                        )}
                                      </p>
                                      <TableContainer>
                                        <Table
                                          className='compliance-clickable-table'
                                          size='small'
                                          aria-label='a dense table'
                                        >
                                          <TableHead>
                                            <TableRow>
                                              <TableCell
                                                style={{ width: "100%" }}
                                                align='center'
                                              >
                                                Transcript ID
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {agentGoodTranscriptData &&
                                              agentGoodTranscriptData.length >
                                                0 &&
                                              agentGoodTranscriptData.map(
                                                (item: any, index: number) => (
                                                  <TableRow
                                                    key={index}
                                                    component='tr'
                                                    sx={{
                                                      "& > *": {
                                                        borderBottom: "unset",
                                                      },
                                                    }}
                                                    onClick={() => {
                                                      getAgentTranscriptData(
                                                        item.transcript_id
                                                      );
                                                    }}
                                                  >
                                                    <TableCell
                                                      style={{ width: "100%" }}
                                                      align='center'
                                                    >
                                                      {item.transcript_id}
                                                    </TableCell>
                                                  </TableRow>
                                                )
                                              )}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </div>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={4}
                                    lg={4}
                                    xl={4}
                                  >
                                    <div className='disclosure_agent_sample_transcripts'>
                                      <p>
                                        Agent Feedback
                                        {mode === "dark" ? (
                                          <span className='shine'>
                                            <AutoAwesomeIcon />
                                          </span>
                                        ) : (
                                          <span className='ai-driven'>
                                            <AutoAwesomeIcon />
                                          </span>
                                        )}
                                      </p>
                                      <TableContainer>
                                        <Table
                                          className='compliance-clickable-table'
                                          size='small'
                                          aria-label='a dense table'
                                        >
                                          <TableHead>
                                            <TableRow>
                                              <TableCell
                                                style={{ width: "100%" }}
                                                align='center'
                                              >
                                                Transcript ID
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {agentBadTranscriptData &&
                                              agentBadTranscriptData.length >
                                                0 &&
                                              agentBadTranscriptData.map(
                                                (item: any, index: number) => (
                                                  <TableRow
                                                    key={index}
                                                    component='tr'
                                                    sx={{
                                                      "& > *": {
                                                        borderBottom: "unset",
                                                      },
                                                    }}
                                                    onClick={() => {
                                                      getAgentTranscriptData(
                                                        item.transcript_id
                                                      );
                                                    }}
                                                  >
                                                    <TableCell
                                                      style={{ width: "100%" }}
                                                      align='center'
                                                    >
                                                      {item.transcript_id}
                                                    </TableCell>
                                                  </TableRow>
                                                )
                                              )}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </div>
                                  </Grid>
                                </Grid>
                              </Item>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                    </Grid>
                  </Item>
                </Grid>
              </Grid>
            </FadeIn>
          )}

          <Grid className='ops-container-wrapper' container spacing={2}>
            <Grid item xs={12}>
              <Item className='ops-card ops-search-section'>
                <div className='ops-search-header'>
                  <h3>Transcript Semantic Search</h3>
                </div>
                <React.StrictMode>
                  <ComplianceSearch
                    parentToChild={{ startDate, endDate, selectedOption, mode }}
                  />
                </React.StrictMode>
              </Item>
            </Grid>
          </Grid>
        </Box>
      </div>

      {selectedTranscript[1].length > 0 && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
          className='modal-global'
        >
          <Box className='modal-style' sx={TranscriptModalStyle}>
            <div className='modal-header compliance-sample-header'>
              <h3>
                <SummarizeIcon /> Full Transcript
              </h3>
              <div className='flex-area transcript-chip'>
                {selectedTranscriptDetails[3] && (
                  <Chip
                    label={
                      "Date: " + formatDateString(selectedTranscriptDetails[3])
                    }
                  />
                )}
                <Chip label={"Reason: " + selectedTranscriptDetails[0]} />
                <Chip label={"Sentiment: " + selectedTranscriptDetails[1]} />
                <Chip label={"Resolved: " + selectedTranscriptDetails[2]} />
              </div>
            </div>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12} lg={7.5} xl={7.5}>
                  <Card variant='outlined' className='get-insights-card'>
                    <CardContent>
                      <Typography
                        id='modal-modal-description transcript-show-modal'
                        sx={{ mt: 2 }}
                      >
                        {selectedTranscript[1].map((segment, index) => (
                          <React.Fragment key={index}>
                            <Highlighter
                              highlightClassName='highlighted-sentence'
                              searchWords={highlightsData.flat()}
                              autoEscape={true}
                              caseSensitive={true}
                              textToHighlight={segment}
                              highlightTag={(props: {
                                children: React.ReactNode;
                              }) => (
                                <Highlight
                                  {...props}
                                  highlightIndex={highlightsData.findIndex(
                                    (highlight: string) =>
                                      highlight.includes(
                                        props.children as string
                                      )
                                  )}
                                />
                              )}
                            />
                            <br />
                            <br />
                          </React.Fragment>
                        ))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={4.5} xl={4.5}>
                  <Card
                    variant='outlined'
                    className='transcript-card transcript-highlighting highlight-legend promo-suggestions'
                  >
                    <p>
                      Promotion Suggestions
                      {mode === "dark" ? (
                        <span className='shine'>
                          <AutoAwesomeIcon />
                        </span>
                      ) : (
                        <span className='ai-driven'>
                          <AutoAwesomeIcon />
                        </span>
                      )}
                    </p>
                    <span>{promoSuggestion}</span>
                  </Card>
                  <Card
                    variant='outlined'
                    className='transcript-card transcript-highlighting highlight-legend'
                  >
                    {highlightsData && highlightsData.length > 0 ? (
                      <CardContent>
                        <div className='legend-flex'>
                          <div>
                            <p>
                              <SquareIcon className='color-prohibit' />{" "}
                              Prohibited Phrases
                            </p>
                            <span>
                              {highlightsData[1][0] === null ? (
                                <span className='green-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='red-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <div>
                            <p>
                              <SquareIcon className='color-action' /> Missed
                              Actioning
                            </p>
                            <span>
                              {highlightsData[2][0] === null ? (
                                <span className='green-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='red-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <div>
                            <p>
                              <SquareIcon className='color-caller' /> Caller
                              Authentication
                            </p>
                            <span>
                              {highlightsData[3][0] === null ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                        <Divider />

                        <div className='legend-flex'>
                          <div>
                            <p>
                              <SquareIcon className='color-oc' /> Scripted
                              Opening
                            </p>
                            <span>
                              {highlightsData[0][0] === null ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <div>
                            <p>
                              <SquareIcon className='color-oc' /> Scripted
                              Closing
                            </p>
                            <span>
                              {highlightsData[0][1] === null ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>

                          <div>
                            <p>
                              <SquareIcon className='color-theme' />
                              Call Theme Disclosure
                            </p>
                            <span>
                              {highlightsData[4][0] === null ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                        <Divider />

                        <div className='legend-flex'>
                          <div>
                            <p>
                              <SquareIcon className='color-record' /> Call
                              Recording Disclosure
                            </p>
                            <span>
                              {highlightsData[5][0] === null ? (
                                <span className='red-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='green-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <div>
                            <p>
                              <SquareIcon className='color-record' /> Consent
                              Failure: Call Recording
                            </p>
                            <span>
                              {highlightsData[5][1] === null ? (
                                <span className='green-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='red-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                          <div>
                            <p>
                              <SquareIcon className='color-theme' /> Consent
                              Failure: Theme Disclosure
                            </p>
                            <span>
                              {highlightsData[4][1] === null ? (
                                <span className='green-arrow'>
                                  <CancelIcon /> Not detected
                                </span>
                              ) : (
                                <span className='red-arrow'>
                                  <CheckCircleIcon /> Detected
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    ) : (
                      <Skeleton width='100%' height='100%' />
                    )}
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Modal>
      )}
    </FadeIn>
  );
}

export default Compliance;
